// Platform-owner access intel: every login across every organization, when,
// what device, from where, plus per-org drill-down and shared/scraped-account
// flags. This is the owner's situational awareness, not a centre's view.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Globe, Monitor, ShieldAlert, Smartphone, Tablet } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

const ROLE_LABEL = { org_admin: 'Centre admin', instructor: 'Instructor', student: 'Student', platform_admin: 'Platform' };
const DEVICE_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor, unknown: Globe };

function ago(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return iso.slice(0, 10);
}

function Device({ device, browser, os }) {
  const Icon = DEVICE_ICON[device] || Globe;
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-500" title={[browser, os].filter(Boolean).join(' · ')}>
      <Icon className="h-3.5 w-3.5" />
      <span className="capitalize">{device || 'unknown'}</span>
      {browser && <span className="text-slate-400">· {browser}</span>}
    </span>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-5 py-4">
      <p className="mono-stat">{label}</p>
      <p className="h-display mt-1 text-3xl tabular-nums text-slate-900">{value ?? '-'}</p>
    </div>
  );
}

function DeviceMix({ mix }) {
  const total = Object.values(mix || {}).reduce((a, b) => a + b, 0) || 1;
  const order = ['mobile', 'desktop', 'tablet', 'unknown'];
  const tone = { mobile: 'bg-indigo-500', desktop: 'bg-slate-700', tablet: 'bg-emerald-500', unknown: 'bg-slate-300' };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-5 py-4">
      <p className="mono-stat">devices · 30 days</p>
      <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
        {order.filter((d) => mix?.[d]).map((d) => (
          <div key={d} className={cn('h-full', tone[d])} style={{ width: `${(mix[d] / total) * 100}%` }} title={`${d}: ${mix[d]}`} />
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {order.filter((d) => mix?.[d]).map((d) => (
          <span key={d} className="inline-flex items-center gap-1.5"><span className={cn('h-2 w-2 rounded-full', tone[d])} /> {d} {Math.round((mix[d] / total) * 100)}%</span>
        ))}
      </div>
    </div>
  );
}

function MemberRow({ m }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm', m.flagged && 'bg-amber-50/50')}>
      <div className="min-w-0 flex-1">
        <span className="font-medium text-slate-900">{m.name || m.email}</span>
        {m.status === 'pending' && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">invite pending</span>}
        {m.flagged && <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800"><ShieldAlert className="h-3 w-3" /> {m.distinct_ips_30d} IPs</span>}
        <span className="ml-0 block truncate text-xs text-slate-400">{m.email}</span>
      </div>
      <Device device={m.last_device} browser={m.last_browser} os={m.last_os} />
      <span className="w-28 text-right text-xs tabular-nums text-slate-400">{m.last_ip || '-'}</span>
      <span className="w-20 text-right text-xs tabular-nums text-slate-400">{m.login_count || 0} logins</span>
      <span className="w-24 text-right text-xs text-slate-500">{ago(m.last_login)}</span>
    </div>
  );
}

function OrgDrill({ orgId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.adminAnalyticsOrg(orgId).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Failed to load'));
  }, [orgId]);
  if (err) return <p className="px-5 py-4 text-sm text-rose-600">{err}</p>;
  if (!data) return <p className="px-5 py-4 text-sm text-slate-400">Loading…</p>;
  const groups = [
    ['Centre admins', data.admins], ['Instructors', data.instructors], ['Students', data.students],
  ];
  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-2 py-3">
      {groups.map(([label, rows]) => (
        <div key={label} className="mb-3 last:mb-0">
          <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label} · {rows.length}</p>
          {rows.length === 0 ? (
            <p className="px-4 text-sm text-slate-400">None yet.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white">
              {rows.map((m, i) => <div key={m.user_id} className={i > 0 ? 'border-t border-slate-50' : ''}><MemberRow m={m} /></div>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminActivity() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openOrg, setOpenOrg] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.adminAnalyticsOverview()).data); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to load activity.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};

  return (
    <AppShell>
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="activity-back">
        <ArrowLeft className="h-4 w-4" /> Platform admin
      </Link>
      <p className="mono-stat mt-6">platform owner</p>
      <h1 className="h-display mt-2 text-3xl">Activity &amp; access</h1>
      <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-slate-500">
        Every login across every organization, when, what device, from where. Accounts that sign in from many
        places get flagged so you can catch sharing or scraping early.
      </p>
      {error && <p className="mt-4 border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm text-red-900">{error}</p>}

      {!data ? <p className="py-10 text-slate-400">Loading…</p> : (
        <>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="logins · 24h" value={k.logins_24h} />
            <Kpi label="logins · 7d" value={k.logins_7d} />
            <Kpi label="active users · 7d" value={k.active_users_7d} />
            <Kpi label="active orgs · 7d" value={k.active_orgs_7d} />
          </div>

          <div className="mt-3"><DeviceMix mix={data.device_mix} /></div>

          {data.flagged?.length > 0 && (
            <section className="mt-8" data-testid="activity-flagged">
              <h2 className="inline-flex items-center gap-2 font-display text-lg font-semibold text-amber-800">
                <ShieldAlert className="h-5 w-5" /> Accounts to watch
              </h2>
              <p className="mt-1 text-sm text-slate-500">Signed in from {4}+ distinct IPs in 30 days, possible shared logins or scraping.</p>
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/40">
                {data.flagged.map((f, i) => (
                  <div key={f.user_id} className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-sm', i > 0 && 'border-t border-amber-100')}>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-slate-900">{f.name || f.email}</span>
                      <span className="ml-2 text-xs text-slate-400">{ROLE_LABEL[f.role] || f.role} · {f.org_name}</span>
                    </div>
                    <span className="text-sm font-semibold text-amber-800">{f.distinct_ips} IPs</span>
                    <span className="text-xs text-slate-500">{f.distinct_devices} devices · {f.logins} logins</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mt-10">
            <h2 className="font-display text-lg font-semibold text-slate-900">By organization</h2>
            {data.per_org.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">No logins recorded yet. Activity appears here as people sign in.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {data.per_org.map((o) => (
                  <div key={o.org_id} className="rounded-lg border border-slate-200 bg-white" data-testid={`activity-org-${o.org_id}`}>
                    <button type="button" onClick={() => setOpenOrg(openOrg === o.org_id ? null : o.org_id)}
                      className="flex w-full items-center gap-4 px-5 py-3.5 text-left hover:bg-slate-50">
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-[15px] font-semibold text-slate-900">{o.org_name}</p>
                        <p className="text-xs text-slate-400">{o.logins} logins · {o.active_users} active users · last {ago(o.last_at)}</p>
                      </div>
                      <ChevronDown className={cn('h-4 w-4 shrink-0 text-slate-400 transition-transform', openOrg === o.org_id && 'rotate-180')} />
                    </button>
                    {openOrg === o.org_id && <OrgDrill orgId={o.org_id} />}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="mt-10">
            <h2 className="font-display text-lg font-semibold text-slate-900">Recent logins</h2>
            <div className="mt-3 rounded-lg border border-slate-200 bg-white">
              {(data.recent || []).map((r, i) => (
                <div key={r.event_id} className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2.5 text-sm', i > 0 && 'border-t border-slate-50')}>
                  <div className="min-w-0 flex-[2]">
                    <span className="font-medium text-slate-900">{r.name || r.email}</span>
                    <span className="ml-2 text-xs text-slate-400">{ROLE_LABEL[r.role] || r.role} · {r.org_name}</span>
                  </div>
                  <Device device={r.device} browser={r.browser} os={r.os} />
                  <span className="w-28 text-right text-xs tabular-nums text-slate-400">{r.ip || '-'}</span>
                  <span className="w-24 text-right text-xs text-slate-500">{ago(r.at)}</span>
                </div>
              ))}
              {(data.recent || []).length === 0 && <p className="px-5 py-4 text-sm text-slate-400">No logins yet.</p>}
            </div>
          </section>
        </>
      )}
    </AppShell>
  );
}
